import React, { useState, useEffect } from 'react';
import { Calendar, Clock, MapPin, User, Copy, Check as CheckIcon, FileText, Phone, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { useMsal } from "@azure/msal-react";
import { createCalendarEvent } from '../lib/msGraph';
import AppointmentCalendar from '../components/AppointmentCalendar';
import DashboardStats from '../components/DashboardStats';

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { accounts } = useMsal();
  const [title, setTitle] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [duration, setDuration] = useState('30');
  const [location, setLocation] = useState('');
  const [loading, setLoading] = useState(false);
  const [meetingLink, setMeetingLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [meetingType, setMeetingType] = useState('phone_interview');
  const [meetingCategory, setMeetingCategory] = useState('phone');
  const [notes, setNotes] = useState('');
  const [meetingDescription, setMeetingDescription] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');

  const meetingTypes = {
    phone_interview: 'Telefoninterview mit TOK Rec2Rec',
    briefing: 'Rücksprache/ Briefing mit TOK Rec2Rec',
    p1: 'P1 - Vorstellungsgespräch',
    p2: 'P2 - Vorstellungsgespräch',
    p3: 'P3 - Vorstellungsgespräch',
    p4: 'P4 - Vorstellungsgespräch',
    p5: 'P5 - Vorstellungsgespräch',
    contract: 'Vertragsgespräch',
    other: 'sonstiges'
  };

  const meetingCategories = {
    phone: 'Telefon',
    video: 'Video-Call',
    inperson: 'persönlich vor Ort'
  };

  useEffect(() => {
    document.title = 'Termin planen | TOK rec2rec Terminplaner';
  }, []);

  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/[^\d]/g, '');
    if (!numbers) return '';
    return `+49${numbers}`;
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    const withoutPrefix = input.startsWith('+49') ? input.slice(3) : input;
    setPhoneNumber(formatPhoneNumber(withoutPrefix));
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Link in die Zwischenablage kopiert!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Fehler beim Kopieren des Links');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Validate that the selected date and time is not in the past
      const selectedDateTime = new Date(`${date}T${time}`);
      const now = new Date();
      now.setSeconds(0, 0); // Reset seconds and milliseconds for fair comparison
      
      if (selectedDateTime < now) {
        toast.error('Der gewählte Zeitpunkt liegt in der Vergangenheit');
        setLoading(false);
        return;
      }

      const localDate = new Date(`${date}T${time}`);
      const dbDate = localDate.toISOString().replace('Z', '');

      // Create meeting in database first
      const { data: meeting, error } = await supabase
        .from('meetings')
        .insert([
          {
            title,
            first_name: firstName,
            last_name: lastName,
            date: dbDate,
            duration: parseInt(duration),
            location,
            created_by: user?.id,
            confirmed: null,
            meeting_type: meetingType,
            meeting_category: meetingCategory,
            notes,
            meeting_description: meetingDescription,
            phone_number: phoneNumber,
            attendees: email ? [email] : undefined,
          },
        ])
        .select()
        .single();

      if (error) {
        console.error('Supabase insert error:', error);
        throw error;
      }

      // Create Microsoft Calendar event if connected
      if (accounts[0]) {
        try {
          const endTime = new Date(localDate.getTime() + parseInt(duration) * 60000);
          const response = await createCalendarEvent({
            subject: title,
            start: localDate,
            end: endTime,
            location,
            description: `Termin mit ${firstName} ${lastName}${meetingDescription ? `\n\n${meetingDescription}` : ''}${phoneNumber ? `\n\nTelefon: ${phoneNumber}` : ''}`,
            attendees: email ? [email] : undefined,
          });

          // Store the Microsoft event ID
          const { error: updateError } = await supabase
            .from('meetings')
            .update({ microsoft_event_id: response.id })
            .eq('id', meeting.id);

          if (updateError) {
            console.error('Error storing Microsoft event ID:', updateError);
            toast.error('Fehler beim Speichern der Microsoft Event ID');
          }

          toast.success('Termin wurde auch im Microsoft Kalender erstellt');
        } catch (error) {
          console.error('Error creating Microsoft calendar event:', error);
          toast.error('Fehler beim Erstellen des Microsoft Kalendereintrags');
        }
      }

      const link = `${window.location.origin}/meeting/${meeting.id}`;
      setMeetingLink(link);

      navigate(`/meeting/${meeting.id}`);
      toast.success('Termin erstellt! Warte auf Bestätigung.');
      
      // Reset form
      setTitle('');
      setFirstName('');
      setLastName('');
      setDate('');
      setTime('');
      setDuration('30');
      setLocation('');
      setMeetingType('phone_interview');
      setMeetingCategory('phone');
      setNotes('');
      setMeetingDescription('');
      setPhoneNumber('');
      setEmail('');
    } catch (error: any) {
      console.error('Error creating meeting:', error);
      toast.error(error.message || 'Fehler beim Erstellen des Termins');
    } finally {
      setLoading(false);
    }
  };

  // Get today's date as minimum date for the date picker
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time to start of day
  const minDate = today.toISOString().split('T')[0];

  return (
    <div className="px-4 py-6 sm:px-0 space-y-6">
      {/* Dashboard Stats */}
      <DashboardStats />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white shadow-lg rounded-lg border-2 border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-6">Termin planen</h2>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                Titel des Termins
              </label>
              <input
                type="text"
                id="title"
                required
                className="mt-1 block w-full rounded-md border-2 border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                  Vorname
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="firstName"
                    required
                    className="block w-full pl-10 rounded-md border-2 border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                  Nachname
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="lastName"
                    required
                    className="block w-full pl-10 rounded-md border-2 border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="hidden">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                E-Mail-Adresse <span className="text-gray-500 text-sm font-normal">(optional)</span>
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  id="email"
                  className="block w-full pl-10 rounded-md border-2 border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="beispiel@domain.de"
                />
              </div>
            </div>

            <div>
              <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700">
                Telefonnummer <span className="text-gray-500 text-sm font-normal">(optional)</span>
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Phone className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="tel"
                  id="phoneNumber"
                  className="block w-full pl-10 rounded-md border-2 border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                  value={phoneNumber}
                  onChange={handlePhoneNumberChange}
                  placeholder="+49"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="date" className="block text-sm font-medium text-gray-700">
                  Datum
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Calendar className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="date"
                    id="date"
                    required
                    min={minDate}
                    className="block w-full pl-10 rounded-md border-2 border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="time" className="block text-sm font-medium text-gray-700">
                  Uhrzeit
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Clock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="time"
                    id="time"
                    required
                    className="block w-full pl-10 rounded-md border-2 border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
              <div>
                <label htmlFor="duration" className="block text-sm font-medium text-gray-700">
                  Dauer (Minuten)
                </label>
                <select
                  id="duration"
                  className="mt-1 block w-full rounded-md border-2 border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                >
                  <option value="15">15 Minuten</option>
                  <option value="30">30 Minuten</option>
                  <option value="45">45 Minuten</option>
                  <option value="60">1 Stunde</option>
                </select>
              </div>

              <div>
                <label htmlFor="meetingType" className="block text-sm font-medium text-gray-700">
                  Art des Termins
                </label>
                <select
                  id="meetingType"
                  className="mt-1 block w-full rounded-md border-2 border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                  value={meetingType}
                  onChange={(e) => setMeetingType(e.target.value)}
                >
                  {Object.entries(meetingTypes).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="meetingCategory" className="block text-sm font-medium text-gray-700">
                  Termin Kategorie
                </label>
                <select
                  id="meetingCategory"
                  className="mt-1 block w-full rounded-md border-2 border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                  value={meetingCategory}
                  onChange={(e) => setMeetingCategory(e.target.value)}
                >
                  {Object.entries(meetingCategories).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="location" className="block text-sm font-medium text-gray-700">
                Ort <span className="text-gray-500 text-sm font-normal">(optional)</span>
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MapPin className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  id="location"
                  className="block w-full pl-10 rounded-md border-2 border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label htmlFor="meetingDescription" className="block text-sm font-medium text-gray-700">
                Öffentliche Beschreibung <span className="text-gray-500 text-sm font-normal">(optional, wird im Kalender angezeigt)</span>
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FileText className="h-5 w-5 text-gray-400" />
                </div>
                <textarea
                  id="meetingDescription"
                  rows={3}
                  className="block w-full pl-10 rounded-md border-2 border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                  value={meetingDescription}
                  onChange={(e) => setMeetingDescription(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                Interne Notizen <span className="text-gray-500 text-sm font-normal">(optional, nur intern sichtbar)</span>
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FileText className="h-5 w-5 text-gray-400" />
                </div>
                <textarea
                  id="notes"
                  rows={3}
                  className="block w-full pl-10 rounded-md border-2 border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border-2 border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Wird erstellt...' : 'Termin erstellen'}
              </button>
            </div>
          </form>

          {meetingLink && (
            <div className="mt-6 p-4 bg-gray-50 rounded-md border-2 border-gray-200">
              <h3 className="text-sm font-medium text-gray-900 mb-2">Bestätigungslink für den Termin:</h3>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  readOnly
                  value={meetingLink}
                  className="flex-1 p-2 text-sm bg-white border-2 border-gray-300 rounded-md"
                />
                <button
                  onClick={() => copyToClipboard(meetingLink)}
                  className="inline-flex items-center px-3 py-2 border-2 border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  {copied ? (
                    <CheckIcon className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4 text-gray-500" />
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        <AppointmentCalendar />
      </div>
    </div>
  );
}